import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Row,
  InputGroup,
  InputGroupText,
} from "reactstrap";
import { useFormik } from "formik";
import { useNavigate, useParams } from "react-router-dom";
import { PageSubHeader } from "components/CommonLayouts/PageSubHeader";
import Footer from "components/VerticalLayout/Footer";
import { get, uri } from "helpers/api_helper";
import * as api from "../../../constants/api_constants";
import { formatToPascal } from "helpers/utils/nameUtils";
import DragAndDropComponent from "components/FileUploadDrag/DragAndDrop";

export function ViewPayment() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [fetchedData, setFetchedData] = useState({});
  const [paymentDocument, setPaymentDocument] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (id) fetchPaymentData(id);
  }, [id]);

  const fetchPaymentData = async (id) => {
    try {
      setIsLoading(true);
      const response = await get(api.GET_PAYMENT_BY_ID(id));
      const paymentData = response?.data;

      // Fetch file details for attachment
      const attachmentId = paymentData?.attachments;
      let fileDetails = null;
      if (attachmentId) {
        try {
          const fileResponse = await get(
            `${uri}/fileservice/api/files/view/${attachmentId}`
          );
          fileDetails = {
            id: fileResponse.id,
            fileName: fileResponse.fileName,
            name: fileResponse.fileName,
            path: fileResponse.path || `/Uploads/${fileResponse.fileName}`,
          };
          setPaymentDocument(fileDetails);
        } catch (error) {
          console.error(`Error fetching file ${attachmentId}:`, error);
          setPaymentDocument(null);
        }
      } else {
        setPaymentDocument(null);
      }

      setFetchedData({
        paidTo: paymentData.paidTo || "",
        name:
          paymentData.vendorName ||
          paymentData.employeeName ||
          paymentData.otherName ||
          "",
        primaryContact: paymentData.primaryContact || "",
        purpose: formatToPascal(paymentData.purpose) || "",
        amountToBePaid: paymentData.amountToBePaid
          ? paymentData.amountToBePaid.toFixed(2)
          : "0.00",
        paidAmount: paymentData.paidAmount
          ? paymentData.paidAmount.toFixed(2)
          : "0.00",
        dueAmount: paymentData.dueAmount
          ? paymentData.dueAmount.toFixed(2)
          : "0.00",
        dueDate: paymentData.dueDate ? paymentData.dueDate.slice(0, 10) : "",
        paidDate: paymentData.paidDate ? paymentData.paidDate.slice(0, 10) : "",
        transactionMode: formatToPascal(paymentData.transactionMode) || "",
        transactionNumber: paymentData.transaction || "",
        attachment: paymentData.attachments || "",
         refundable: paymentData.refundable && paymentData.refundable.toLowerCase() === "yes" ? "YES" : "NO",
        status: formatToPascal(paymentData.status) || "",
        notes: paymentData.notes || "",
        currency: paymentData.currency || "USD",
        invoice: paymentData.invoice || "",
      });
    } catch (error) {
      console.error("Error fetching payment data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: fetchedData,
  });

  if (isLoading) {
    return <div className="text-center py-3">Loading payment details...</div>;
  }

  return (
    <div className="page-content">
      <Container fluid className="py-0 px-0">
        <PageSubHeader
          heading="View Payment"
          routePath="Home"
          pageName="Payments"
          backPath="/payments"
        />

        <Row className="px-4 mx-0 my-3">
          <Col xs={12} md={12} className="p-0 m-0">
            <Card>
              <CardBody>
                <Form className="row g-3 needs-validation py-3">
                  <Row className="card-group">
                    <Col xs={12} className="card-header">
                      <p className="form-header">Payable Information</p>
                    </Col>
                    <Row className="card-group-body">
                      {/* Paid To Field */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidTo">Paid To</Label>
                          <Input
                            name="paidTo"
                            type="text"
                            className="form-control"
                            value={formik.values.paidTo}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Name Field */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="name">
                            {formik.values.paidTo === "VENDOR"
                              ? "Vendor Name"
                              : formik.values.paidTo === "EMPLOYEE"
                              ? "Employee Name"
                              : "Name"}
                          </Label>
                          <Input
                            name="name"
                            type="text"
                            className="form-control"
                            value={formik.values.name}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Invoice Number */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="invoice">Invoice Number</Label>
                          <Input
                            name="invoice"
                            type="text"
                            className="form-control"
                            value={formik.values.invoice}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Primary Contact */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="primaryContact">
                            Primary Contact
                          </Label>
                          <Input
                            name="primaryContact"
                            type="text"
                            className="form-control"
                            value={formik.values.primaryContact}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Currency */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="currency">Currency</Label>
                          <Input
                            name="currency"
                            type="text"
                            className="form-control"
                            value={formik.values.currency}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Amount Fields */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="amountToBePaid">
                            Amount To Be Paid
                          </Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <Input
                              name="amountToBePaid"
                              type="text"
                              className="form-control"
                              value={formik.values.amountToBePaid}
                              disabled
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidAmount">Paid Amount</Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <Input
                              name="paidAmount"
                              type="text"
                              className="form-control"
                              value={formik.values.paidAmount}
                              disabled
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="dueAmount">Due Amount</Label>
                          <InputGroup>
                            <InputGroupText>
                              {formik.values.currency || "USD"}
                            </InputGroupText>
                            <Input
                              name="dueAmount"
                              type="text"
                              className="form-control"
                              value={formik.values.dueAmount}
                              disabled
                            />
                          </InputGroup>
                        </FormGroup>
                      </Col>

                      {/* Date Fields */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="paidDate">Paid Date</Label>
                          <Input
                            name="paidDate"
                            type="date"
                            className="form-control"
                            value={formik.values.paidDate}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="dueDate">Due Date</Label>
                          <Input
                            name="dueDate"
                            type="date"
                            className="form-control"
                            value={formik.values.dueDate}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Purpose */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="purpose">Purpose</Label>
                          <Input
                            name="purpose"
                            type="text"
                            className="form-control"
                            value={formik.values.purpose}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Transaction Details */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="transactionMode">
                            Transaction Mode
                          </Label>
                          <Input
                            name="transactionMode"
                            type="text"
                            className="form-control"
                            value={formik.values.transactionMode}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="transactionNumber">
                            Transaction Number
                          </Label>
                          <Input
                            name="transactionNumber"
                            type="text"
                            className="form-control"
                            value={formik.values.transactionNumber}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Status */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="status">Status</Label>
                          <Input
                            name="status"
                            type="text"
                            className="form-control"
                            value={formik.values.status}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Refundable */}
                      <Col md={4}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="refundable">Refundable</Label>
                          <Input
                            name="refundable"
                            type="checkbox"
                            className="form-check-input"
                            style={{
                              width: "20px",
                              height: "20px",
                              borderWidth: "1px",
                              borderColor: "#000",
                            }}
                            checked={formik.values.refundable === "YES"}
                            disabled
                          />
                        </FormGroup>
                      </Col>

                      {/* Notes */}
                      <Col md={12}>
                        <FormGroup className="mb-3">
                          <Label htmlFor="notes">Notes</Label>
                          <Input
                            name="notes"
                            type="textarea"
                            className="form-control"
                            value={formik.values.notes}
                            disabled
                          />
                        </FormGroup>
                      </Col>
                      <Row>
                        
                          {/* Attachment */}
                          <Col md={4}>
                            <FormGroup className="mb-3">
                              <Label htmlFor="attachment">Attachment</Label>
                              {paymentDocument ? (
                                <div className="position-relative rounded py-2 pe-4">
                                  <DragAndDropComponent
                                    initialFile={paymentDocument}
                                    readOnly={true}
                                  />
                                </div>
                              ) : (
                                <div>No attachment available</div>
                              )}
                            </FormGroup>
                          </Col>
                        
                      </Row>
                    </Row>
                  </Row>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
        <Footer
          save={() => navigate(`/add-payment/${id}`)}
          saveText="Edit Payment"
          showBackConfirmation={false}
        />
      </Container>
    </div>
  );
}

export default ViewPayment;
